import axiosInstance from '../../api/axios';
import { performTokenRefresh } from '../../api/oauth/refreshFlow.ts';

/**
 * Runs the attribute-centric pipeline for a control and streams progress.
 *
 * 1. Create the attribute_test_runs shell (reuses backend create endpoint).
 * 2. Open the SSE execute-stream and forward progress to onProgress.
 * 3. Resolve with the final result (also persisted server-side).
 *
 * Parallel to RunTestFlow's runner — does not touch the existing step pipeline.
 */
export async function runAttributeTest({
  controlUuid,
  controlId,
  testingPeriodStart,
  testingPeriodEnd,
  additionalContext,
  onProgress,
  signal,
  existingRunId,
  auditYearId,
  logRawCalls,
  analysisMode,
}) {
  const isoDate = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);

  // 1. Reuse an existing attribute run (e.g. from the evidence dashboard) or
  //    create a fresh shell.
  let runId = existingRunId;
  if (!runId) {
    const createResp = await axiosInstance.post('/api/attribute-test-runs', {
      sox_control_id: controlUuid,
      control_id: controlId,
      testing_period_start: isoDate(testingPeriodStart),
      testing_period_end: isoDate(testingPeriodEnd),
      additional_context: additionalContext || undefined,
      audit_year_id: auditYearId || undefined,
    });
    runId = createResp.data.id;
  }

  // 1b. Freeze plan snapshot + start execution record
  try {
    await axiosInstance.post(`/api/control-test-runs/${runId}/prepare-execution`);
  } catch (prepErr) {
    // 403: no module access. 422: the server says the run is not ready to execute (no
    // samples / population / plan) or is already signed off — its message says what to do.
    const status = prepErr?.response?.status;
    if (status === 403 || status === 422) throw prepErr;
    /* non-fatal if already prepared */
  }

  // 2. Stream execution
  const streamPayload = {
    control_id: controlUuid,
    run_id: runId,
    additional_context: additionalContext || null,
    testing_period_start: isoDate(testingPeriodStart) || null,
    testing_period_end: isoDate(testingPeriodEnd) || null,
    // Keep the text of every model call with the run's activity log (prompts, tool output, responses).
    ...(logRawCalls ? { log_raw_calls: true } : {}),
    // 'fast': one AI call per sample for all attributes; 'exhaustive': one checking session per sample × attribute.
    // Left out, the AI service uses its default.
    ...(analysisMode === 'fast' || analysisMode === 'exhaustive' ? { analysis_mode: analysisMode } : {}),
  };
  const streamConfig = { adapter: 'fetch', responseType: 'stream', validateStatus: () => true, timeout: 1200000, signal };

  let response = await axiosInstance.post('/api/attribute-test-runs/execute-stream', streamPayload, streamConfig);

  if (response.status === 401) {
    // Token likely expired — refresh and retry once (the interceptor is bypassed by validateStatus)
    await performTokenRefresh();
    response = await axiosInstance.post('/api/attribute-test-runs/execute-stream', streamPayload, streamConfig);
  }

  // 403: module access. 422: run not ready / already signed off. Both carry a message.
  if (response.status === 403 || response.status === 422) {
    let message = 'Attribute test failed';
    try {
      const text = await new Response(response.data).text();
      const parsed = JSON.parse(text);
      message = parsed.error || parsed.message || parsed.detail || message;
    } catch { /* keep default */ }
    throw new Error(message);
  }

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.data.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const normalized = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const events = normalized.split('\n\n');
    buffer = normalized.endsWith('\n\n') ? '' : events.pop() || '';

    for (const eventText of events.filter((e) => e.trim())) {
      let eventType = 'message';
      let data = null;
      for (const line of eventText.split('\n')) {
        if (line.startsWith('event: ')) eventType = line.slice(7).trim();
        else if (line.startsWith('data: ')) {
          try { data = JSON.parse(line.slice(6)); } catch { /* ignore partial */ }
        }
      }
      if (!data) continue;
      if (eventType === 'complete' || data.stage === 'complete') {
        finalResult = data.result || null;
      } else if (eventType === 'error' || data.stage === 'error') {
        throw new Error(data.error || data.message || 'Attribute test failed');
      } else if (onProgress) {
        onProgress(data);
      }
    }
  }

  return { runId, result: finalResult };
}
